<template>
  <v-app :class="{ 'theme--dark': isDarkMode }">
    <HeaderDealerDashboard
      :userName="userName"
      :businessName="businessName"
      :isDarkModeProp="isDarkMode"
      @navigation="handleNavigation"
    />

    <v-main class="dealer-main" :class="{ 'dark-mode': isDarkMode }">
      <v-container fluid class="pa-6">
        <!-- Page Header -->
        <div class="d-flex justify-space-between align-center mb-6">
          <div>
            <h1 class="text-h4 font-weight-bold primary--text">Payouts</h1>
            <p class="text-subtitle-1 text-grey-600 mt-2">Track your commission payments and earnings</p>
          </div>
          <div class="d-flex align-center">
            <v-btn color="green" dark class="mr-3" @click="openInvoiceDialog">
              <span class="material-icons-round mr-2">receipt</span>
              Generate Invoice
            </v-btn>
            <v-btn color="primary" outlined class="mr-3" @click="exportData">
              <span class="material-icons-round mr-2">file_download</span>
              Export
            </v-btn>
            <v-btn color="blue" dark @click="downloadReport">
              <span class="material-icons-round mr-2">description</span>
              Download Report
            </v-btn>
          </div>
        </div>

        <!-- Statistics Cards -->
        <v-row class="mb-6">
          <v-col cols="12" sm="6" md="3">
            <v-card elevation="3" class="pa-4 text-center">
              <div class="text-h6 font-weight-bold text-grey-700 mb-2">Pending Commissions</div>
              <div class="text-h4 font-weight-bold primary--text">${{ formatCurrency(stats.pendingCommissions) }}</div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card elevation="3" class="pa-4 text-center">
              <div class="text-h6 font-weight-bold text-grey-700 mb-2">Paid This Month</div>
              <div class="text-h4 font-weight-bold success--text">${{ formatCurrency(stats.paidThisMonth) }}</div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card elevation="3" class="pa-4 text-center">
              <div class="text-h6 font-weight-bold text-grey-700 mb-2">Avg Processing Time</div>
              <div class="text-h4 font-weight-bold info--text">{{ stats.avgProcessingTime }} days</div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card elevation="3" class="pa-4 text-center">
              <div class="text-h6 font-weight-bold text-grey-700 mb-2">Disputes</div>
              <div class="text-h4 font-weight-bold error--text">{{ stats.disputes }} open</div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Payouts Table -->
        <v-card elevation="3">
          <v-card-title class="text-h5 font-weight-bold">
            <span class="material-icons-round mr-2">payment</span>
            Commission History
          </v-card-title>
          <v-card-text class="pa-0">
            <div v-if="loading" class="text-center pa-8">
              <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
              <div class="mt-4 text-h6">Loading payouts...</div>
            </div>

            <div v-else-if="payouts.length === 0" class="text-center pa-8">
              <span class="material-icons-round" style="font-size: 64px; color: #ccc;">payment</span>
              <div class="mt-4 text-h6 text-grey-600">No payouts found</div>
              <div class="text-body-2 text-grey-500">Commission payments will appear here once processed</div>
            </div>

            <div v-else class="commission-table-wrapper" :class="{ 'dark-mode-table': isDarkMode }">
              <v-data-table
                :headers="headers"
                :items="payouts"
                :items-per-page="10"
                class="elevation-0 commission-history-table"
                :loading="loading"
                :dark="isDarkMode"
              >
                <template v-slot:item.date="{ item }">
                  <span class="table-text">{{ formatDate(item.date) }}</span>
                </template>

                <template v-slot:item.referring_agent="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="32" color="primary" class="mr-3">
                      <span class="white--text text-caption">{{ getInitials(item.referring_agent) }}</span>
                    </v-avatar>
                    <span class="table-text">{{ item.referring_agent }}</span>
                  </div>
                </template>

                <template v-slot:item.vehicle="{ item }">
                  <div>
                    <div class="table-text">{{ item.vehicle }}</div>
                    <div class="table-text-secondary">{{ item.vehicle_details || '' }}</div>
                  </div>
                </template>

                <template v-slot:item.commission="{ item }">
                  <span class="table-text font-weight-bold">${{ formatCurrency(item.commission) }}</span>
                </template>

                <template v-slot:item.status="{ item }">
                  <v-chip
                    :color="getStatusColor(item.status)"
                    dark
                    small
                  >
                    <span class="material-icons-round mr-1" style="font-size: 14px;">
                      {{ getStatusIcon(item.status) }}
                    </span>
                    {{ item.status }}
                  </v-chip>
                </template>
              </v-data-table>
            </div>
          </v-card-text>
        </v-card>
      </v-container>
    </v-main>

    <FooterDealer />

    <!-- Generate Invoice Dialog -->
    <v-dialog v-model="showGenerateInvoiceDialog" max-width="600">
      <v-card>
        <v-card-title class="text-h5 success--text">
          <span class="material-icons-round mr-2">receipt</span>
          Generate Payout Invoice
        </v-card-title>
        <v-card-text>
          <v-form ref="invoiceForm" v-model="invoiceFormValid">
            <v-row>
              <v-col cols="12">
                <v-select
                  v-model="invoiceForm.commissionIds"
                  :items="pendingCommissions"
                  item-text="display_text"
                  item-value="id"
                  label="Select Pending Commissions"
                  multiple
                  chips
                  deletable-chips
                  :rules="[v => (v && v.length > 0) || 'Please select at least one commission']"
                  outlined
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip v-if="index < 2" small>
                      ${{ formatCurrency(item.amount) }} - {{ item.agent_name }}
                    </v-chip>
                    <span v-if="index === 2" class="text-caption grey--text">
                      (+{{ invoiceForm.commissionIds.length - 2 }} others)
                    </span>
                  </template>
                </v-select>
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="invoiceForm.paymentMethod"
                  :items="paymentMethods"
                  label="Payment Method"
                  :rules="[v => !!v || 'Payment method is required']"
                  outlined
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="invoiceForm.totalAmount"
                  label="Total Amount"
                  prefix="$"
                  readonly
                  outlined
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="invoiceForm.notes"
                  label="Notes (Optional)"
                  placeholder="Add any additional notes for this payout request..."
                  rows="3"
                  outlined
                ></v-textarea>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="closeInvoiceDialog">Cancel</v-btn>
          <v-btn
            color="green"
            dark
            @click="generateInvoice"
            :loading="generateInvoiceLoading"
            :disabled="!invoiceFormValid"
          >
            <span class="material-icons-round mr-2">receipt</span>
            Generate Invoice
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
import HeaderDealerDashboard from '../Layout/HeaderDealerDashboard.vue'
import FooterDealer from '../Layout/FooterDealer.vue'

export default {
  name: 'DealerPayouts',
  components: {
    HeaderDealerDashboard,
    FooterDealer
  },
  data() {
    return {
      isDarkMode: true,
      loading: false,
      userName: '',
      businessName: '',
      stats: {
        pendingCommissions: 0,
        paidThisMonth: 0,
        avgProcessingTime: 0,
        disputes: 0
      },
      payouts: [],
      headers: [
        { text: 'Date', value: 'date', sortable: true },
        { text: 'Referring Agent', value: 'referring_agent', sortable: true },
        { text: 'Vehicle', value: 'vehicle', sortable: true },
        { text: 'Commission', value: 'commission', sortable: true, align: 'end' },
        { text: 'Status', value: 'status', sortable: true, align: 'center' }
      ],
      showGenerateInvoiceDialog: false,
      generateInvoiceLoading: false,
      invoiceFormValid: false,
      invoiceForm: {
        commissionIds: [],
        paymentMethod: 'bank_transfer',
        totalAmount: '0.00',
        notes: ''
      },
      pendingCommissions: [],
      paymentMethods: [
        { text: 'Bank Transfer', value: 'bank_transfer' },
        { text: 'PayPal', value: 'paypal' },
        { text: 'Check', value: 'check' },
        { text: 'Other', value: 'other' }
      ]
    }
  },
  beforeCreate() {
    const token = localStorage.getItem('token')
    if (!token) {
      this.$router.push('/dealers/login')
    }
  },
  async mounted() {
    this.loadDarkModePreference()
    this.loadUserData()
    await this.loadPayouts()
  },
  computed: {
    selectedCommissionsTotal() {
      if (!this.invoiceForm.commissionIds || this.invoiceForm.commissionIds.length === 0) {
        return 0
      }
      return this.pendingCommissions
        .filter(commission => this.invoiceForm.commissionIds.includes(commission.id))
        .reduce((total, commission) => total + parseFloat(commission.amount), 0)
    }
  },
  watch: {
    selectedCommissionsTotal(newTotal) {
      this.invoiceForm.totalAmount = this.formatCurrency(newTotal)
    }
  },
  methods: {
    handleNavigation() {
      // Navigation is handled by the HeaderDealerDashboard component
    },
    loadDarkModePreference() {
      const saved = localStorage.getItem('dealer_dark_mode')
      this.isDarkMode = saved !== null ? saved === 'true' : true
    },
    loadUserData() {
      this.userName = localStorage.getItem('user_name') || 'Dealer'
      this.businessName = localStorage.getItem('business_name') || 'Your Business'
    },
    async loadPayouts() {
      try {
        this.loading = true
        const token = localStorage.getItem('token')

        const response = await axios.get('/api/dealer/payouts', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.data.flag === 1) {
          this.payouts = response.data.data.payouts || []
          const apiStats = response.data.data.stats || {}
          this.stats = {
            pendingCommissions: apiStats.pending_commissions || 0,
            paidThisMonth: apiStats.paid_this_month || 0,
            avgProcessingTime: apiStats.avg_processing_time || 0,
            disputes: apiStats.disputes || 0
          }

          // Load pending commissions for invoice generation
          await this.loadPendingCommissions()
        } else {
          this.payouts = []
          this.$toast.error(response.data.message || 'Error loading payouts')
        }
      } catch (error) {
        console.error('Error loading payouts:', error)
        this.$toast.error('Error loading payout data')
        this.payouts = []
      } finally {
        this.loading = false
      }
    },
    formatCurrency(amount) {
      const numAmount = Number(amount) || 0
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(numAmount)
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: '2-digit'
      })
    },
    getInitials(name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase()
    },
    getStatusColor(status) {
      switch (status.toLowerCase()) {
        case 'paid': return 'success'
        case 'pending': return 'warning'
        case 'disputed': return 'error'
        case 'processing': return 'info'
        default: return 'grey'
      }
    },
    getStatusIcon(status) {
      switch (status.toLowerCase()) {
        case 'paid': return 'check_circle'
        case 'pending': return 'schedule'
        case 'disputed': return 'error'
        case 'processing': return 'sync'
        default: return 'help'
      }
    },
    async exportData() {
      try {
        this.$toast.info('Exporting payout data...')
        const token = localStorage.getItem('token')

        const response = await axios.post('/api/dealer/export-payouts', {}, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.data.flag === 1) {
          this.$toast.success('Export completed successfully!')
          // In a real implementation, this would trigger a file download
        } else {
          this.$toast.error('Export failed')
        }
      } catch (error) {
        console.error('Error exporting data:', error)
        this.$toast.error('Error exporting payout data')
      }
    },
    downloadReport() {
      // Generate a simple CSV download for now
      const csvContent = this.generateCSV()
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `payouts_report_${new Date().toISOString().split('T')[0]}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      this.$toast.success('Report downloaded successfully!')
    },
    generateCSV() {
      const headers = ['Date', 'Referring Agent', 'Vehicle', 'Commission', 'Status']
      const csvRows = [headers.join(',')]

      this.payouts.forEach(payout => {
        const row = [
          payout.date,
          `"${payout.referring_agent}"`,
          `"${payout.vehicle}"`,
          payout.commission,
          payout.status
        ]
        csvRows.push(row.join(','))
      })

      return csvRows.join('\n')
    },
    async loadPendingCommissions() {
      try {
        const token = localStorage.getItem('token')

        const response = await axios.get('/api/dealer/pending-commissions', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.data.flag === 1) {
          this.pendingCommissions = response.data.data.commissions || []
        } else {
          this.pendingCommissions = []
        }
      } catch (error) {
        console.error('Error loading pending commissions:', error)
        this.pendingCommissions = []
      }
    },
    async openInvoiceDialog() {
      // Refresh pending commissions when opening dialog
      await this.loadPendingCommissions()
      this.invoiceForm = {
        commissionIds: [],
        paymentMethod: 'bank_transfer',
        totalAmount: '0.00',
        notes: ''
      }
      this.showGenerateInvoiceDialog = true
    },
    closeInvoiceDialog() {
      this.showGenerateInvoiceDialog = false
      this.invoiceForm = {
        commissionIds: [],
        paymentMethod: 'bank_transfer',
        totalAmount: '0.00',
        notes: ''
      }
      this.$refs.invoiceForm?.resetValidation()
    },
    async generateInvoice() {
      try {
        if (!this.$refs.invoiceForm.validate()) {
          return
        }

        this.generateInvoiceLoading = true
        const token = localStorage.getItem('token')

        const response = await axios.post('/api/dealer/generate-payout-invoice', {
          commission_ids: this.invoiceForm.commissionIds,
          payment_method: this.invoiceForm.paymentMethod,
          total_amount: this.selectedCommissionsTotal,
          notes: this.invoiceForm.notes
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.data.flag === 1) {
          this.$toast.success('Payout invoice generated successfully!')
          this.closeInvoiceDialog()

          // Reload payouts to show the new invoice
          await this.loadPayouts()
        } else {
          this.$toast.error(response.data.message || 'Error generating invoice')
        }
      } catch (error) {
        console.error('Error generating invoice:', error)
        this.$toast.error('Error generating payout invoice')
      } finally {
        this.generateInvoiceLoading = false
      }
    }
  }
}
</script>

<style scoped>
.dealer-main {
  background-color: #f5f5f5;
  min-height: 100vh;
}
.dealer-main.dark-mode {
  background: url('~@/assets/img/skyline-bg-dark.png') no-repeat center bottom;
  background-size: cover;
  background-color: #0f0f23;
}

.v-card {
  border-radius: 12px;
}

.v-data-table >>> .v-data-table__wrapper {
  border-radius: 0 0 12px 12px;
}

.agent-item {
  transition: background-color 0.2s ease;
}

.agent-item:hover {
  background-color: #f5f5f5;
}
</style>

<style>
/* Commission History Table - Light Mode */
.commission-table-wrapper:not(.dark-mode-table) .commission-history-table {
  background-color: #fff !important;
}
.commission-table-wrapper:not(.dark-mode-table) .commission-history-table th,
.commission-table-wrapper:not(.dark-mode-table) .commission-history-table td {
  color: #212121 !important;
  background-color: #fff !important;
}
.commission-table-wrapper:not(.dark-mode-table) .table-text {
  color: #212121 !important;
}
.commission-table-wrapper:not(.dark-mode-table) .table-text-secondary {
  color: #757575 !important;
}
.commission-table-wrapper:not(.dark-mode-table) .v-data-table-header th {
  color: #424242 !important;
  background-color: #fafafa !important;
}
.commission-table-wrapper:not(.dark-mode-table) .v-data-footer {
  color: #212121 !important;
}

/* Commission History Table - Dark Mode */
.commission-table-wrapper.dark-mode-table .table-text {
  color: #e0e0e0 !important;
}
.commission-table-wrapper.dark-mode-table .table-text-secondary {
  color: #b0b0b0 !important;
}

/* Light mode - ensure text visibility */
.dealer-main:not(.dark-mode) .v-data-table th,
.dealer-main:not(.dark-mode) .v-data-table td,
.dealer-main:not(.dark-mode) .v-data-table span:not(.white--text):not(.v-chip span),
.dealer-main:not(.dark-mode) .v-data-table div:not(.v-chip),
.dealer-main:not(.dark-mode) .v-data-table .font-weight-medium,
.dealer-main:not(.dark-mode) .v-data-table .font-weight-bold,
.dealer-main:not(.dark-mode) .v-list-item__title,
.dealer-main:not(.dark-mode) .v-list-item__subtitle {
  color: #212121 !important;
}
.dealer-main:not(.dark-mode) .text-grey-600,
.dealer-main:not(.dark-mode) .v-data-table .text-caption:not(.white--text) {
  color: #757575 !important;
}
.dealer-main:not(.dark-mode) .v-icon,
.dealer-main:not(.dark-mode) .material-icons-round {
  color: #757575 !important;
}
.dealer-main:not(.dark-mode) .v-btn .v-icon,
.dealer-main:not(.dark-mode) .v-btn .material-icons-round,
.dealer-main:not(.dark-mode) .v-chip .material-icons-round,
.dealer-main:not(.dark-mode) .v-avatar .white--text,
.dealer-main:not(.dark-mode) .v-chip,
.dealer-main:not(.dark-mode) .v-chip span {
  color: inherit !important;
}

/* Dark mode styles - unscoped for proper cascading */
.dark-mode .v-card {
  background: rgba(30, 30, 60, 0.8) !important;
  border: 1px solid rgba(138, 43, 226, 0.3);
}
/* Text colors */
.dark-mode .v-card-title,
.dark-mode .v-card-text,
.dark-mode .v-card-text *,
.dark-mode .v-data-table,
.dark-mode .v-data-table th,
.dark-mode .v-data-table td,
.dark-mode .v-list-item__title,
.dark-mode .v-list-item__subtitle,
.dark-mode .v-list-item__content,
.dark-mode .stat-value,
.dark-mode .stat-label,
.dark-mode .text-h4,
.dark-mode .text-h5,
.dark-mode .text-h6,
.dark-mode .text-subtitle-1,
.dark-mode .text-subtitle-2,
.dark-mode .text-body-1,
.dark-mode .text-body-2,
.dark-mode .text-caption,
.dark-mode strong,
.dark-mode label,
.dark-mode .v-label,
.dark-mode .v-messages__message,
.dark-mode .v-input__slot,
.dark-mode .v-text-field input,
.dark-mode .v-textarea textarea,
.dark-mode .v-select__selection,
.dark-mode .v-radio .v-label,
.dark-mode .v-checkbox .v-label,
.dark-mode .v-switch .v-label {
  color: #e0e0e0 !important;
}
/* Icons */
.dark-mode .v-icon,
.dark-mode .material-icons,
.dark-mode .material-icons-round,
.dark-mode .material-icons-outlined {
  color: #e0e0e0 !important;
}
/* Button icons should keep their color */
.dark-mode .v-btn .v-icon,
.dark-mode .v-btn .material-icons,
.dark-mode .v-btn .material-icons-round {
  color: inherit !important;
}
/* Colored text overrides */
.dark-mode .v-card-title.primary--text {
  color: #42a5f5 !important;
}
.dark-mode .primary--text {
  color: #42a5f5 !important;
}
.dark-mode .v-data-table__empty-wrapper {
  color: rgba(255,255,255,0.5) !important;
}
.dark-mode .agent-item:hover {
  background-color: rgba(138, 43, 226, 0.1);
}
</style>
